import { useMemo, useState } from 'react'
import {
  TEAM_GROUPS, TEAM_NAMES, TEAM_COUNT, MAP_META, REP_COUNT,
  proposalFor, needsAssignment, currentRep,
  confidenceMeta, assignmentHistory,
} from '../assignments.js'
import { createPortal } from 'react-dom'
import { useAccessOptional } from '../../../lib/access.jsx'
import { pickerMode, partitionForLane, laneOptions, LANE_DELEGATE_LABEL } from '../laneDelegation.js'
import styles from './assignments.module.css'

/**
 * THE ASSIGNMENT SURFACES — a suggestion, a picker, a bulk bar, a history.
 *
 * The split down the middle of this file is the governance rule:
 *
 *   SuggestedRep  reads the engine and DRAWS. It never writes, and it renders
 *                 nothing at all for a record that already has an owner.
 *   RepPicker     writes — and only ever from a human click, only ever for a
 *                 human holding "Manage Assignments".
 *
 * Every proposal states WHY it was made and how confident it is, because a
 * routing suggestion a rep can't audit is one they'll rightly ignore.
 */

/* ---- the proposal (display only) ------------------------------------- */

/**
 * "Suggested rep: X — why", with the confidence badge and up to two alternates.
 * Returns null for an owned record: the engine does not second-guess a human's
 * assignment, so there is nothing to draw.
 */
export function SuggestedRep({ record, compact = false }) {
  const proposal = useMemo(() => proposalFor(record), [record])
  if (!proposal) return null

  if (proposal.noMatch) {
    return (
      <span className={`${styles.suggest} ${styles.noMatch} ${compact ? styles.compact : ''}`}>
        <span className={styles.badge} style={{ color: confidenceMeta('none').color }}>no match</span>
        <span className={styles.suggestText}>No proven match — needs manual assignment.</span>
      </span>
    )
  }

  const cm = confidenceMeta(proposal.confidence)
  return (
    <span className={`${styles.suggest} ${compact ? styles.compact : ''}`} title={proposal.why}>
      <span className={styles.badge} style={{ color: cm.color, borderColor: `${cm.color}55` }}>
        {proposal.confidence}
      </span>
      <span className={styles.suggestText}>
        <strong className={styles.suggestName}>Suggested rep: {proposal.suggestedDisplayName}</strong>
        {!compact && <> — {proposal.why}</>}
      </span>
      {proposal.needsBackup && (
        <span className={styles.flag} title="Single-point dependency — this line sits on one rep. Assign a backup.">
          needs backup
        </span>
      )}
      {!compact && proposal.alternates?.length > 0 && (
        <span className={styles.alts}>
          also: {proposal.alternates.map((a) => `${a.rep} (${a.why || `score ${a.score}`})`).join(' · ')}
        </span>
      )}
    </span>
  )
}

/* ---- the picker (the write) ------------------------------------------ */

/**
 * THE OPTION LIST — the whole SR team, grouped by what they do.
 *
 * The suggestion above comes from the win history (proven reps only); the
 * PICKER offers everybody, because a record isn't only ever a rep's job:
 * artwork goes to a designer, a gym partnership to the cheer team. Grouping by
 * category is what keeps 34 names findable in one dropdown.
 *
 * `current` is the value already on the record. A legacy or off-roster name is
 * appended rather than dropped — blanking somebody's assignment because they
 * left the roster would misreport who owns the record.
 */
function TeamOptions({ current = '' }) {
  const onRoster = current && TEAM_NAMES.includes(current)
  return (
    <>
      {TEAM_GROUPS.map((g) => (
        <optgroup key={g.group} label={g.group}>
          {g.members.map((m) => (
            <option key={m.id} value={m.name} title={m.role ? `${m.category}` : m.category}>
              {m.name}
            </option>
          ))}
        </optgroup>
      ))}
      {current && !onRoster && (
        <optgroup label="Currently assigned (not on the roster)">
          <option value={current}>{current}</option>
        </optgroup>
      )}
    </>
  )
}

/**
 * The rep picker. Options are the full team roster — a name is never typed.
 *
 * It opens PRE-SET to the engine's suggestion for an unowned record, so the
 * common case is one click; on an owned record it opens on the current owner,
 * because overriding somebody's assignment should be a deliberate change, not
 * a default.
 *
 * `gate` is the resolved "Manage Assignments" decision. Denied renders the
 * owner read-only WITH the reason — never a button that quietly does nothing.
 */
export function RepPicker({ record, gate, onAssign, busy = false, autoSuggest = true }) {
  const owner = currentRep(record)
  /* CHEER LANE DELEGATION. A viewer without Manage Assignments but holding
   * `cc_lane_delegate` gets a picker on lane records only, offering the lane
   * members only. `full` is the manager path and is unchanged. */
  const access = useAccessOptional()
  const mode = useMemo(
    () => pickerMode(gate, access?.laneDelegation, record),
    [gate, access?.laneDelegation, record]
  )
  const suggestion = useMemo(
    () => (autoSuggest && needsAssignment(record) ? proposalFor(record) : null),
    [record, autoSuggest]
  )
  const [value, setValue] = useState(
    () => suggestion?.suggestedDisplayName || owner || ''
  )
  // Authorized lane moves need no note. Other assignment paths retain their reason gate.
  const [reason, setReason] = useState('')
  const dirty = value && value !== owner
  const reasonText = reason.trim()
  const ready = dirty && (mode.mode === 'lane' || reasonText.length > 0)

  if (mode.mode === 'readonly') {
    return (
      <span className={styles.readonly} title={mode.why || gate?.why || ''}>
        {owner || <span className={styles.gap}>Unassigned</span>}
        <span className={styles.lock} aria-hidden="true">🔒</span>
      </span>
    )
  }

  return (
    <span className={styles.picker} onClick={(e) => e.stopPropagation()}>
      <select
        className={styles.select}
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        aria-label={`Assigned rep for ${record?.name || 'this record'}`}
      >
        {mode.mode === 'lane' ? (
          /* No blank option: a lane delegate cannot clear a seat (the server
           * answers lane_cannot_clear), so the list is the lane and nothing
           * else. */
          <optgroup label={`${LANE_DELEGATE_LABEL}`}>
            {mode.options.map((n) => (
              <option key={n} value={n}>{n === owner ? `${n} (current)` : n}</option>
            ))}
          </optgroup>
        ) : (
          <>
            <option value="">{owner ? `${owner} (current)` : '— unassigned —'}</option>
            <TeamOptions current={owner} />
          </>
        )}
      </select>
      {dirty && (
        <input
          type="text"
          className={styles.reason}
          value={reason}
          disabled={busy}
          maxLength={200}
          placeholder={mode.mode === 'lane' ? 'Note (optional)' : owner ? 'Why the change? (required)' : 'Why this rep? (required)'}
          aria-label={`Reason for assigning ${record?.name || 'this record'} to ${value}`}
          title="Written to the permanent assignment ledger with your name and the time."
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && ready && !busy) onAssign?.(record, value, reasonText) }}
        />
      )}
      <button
        type="button"
        className={styles.assignBtn}
        disabled={busy || !ready}
        onClick={() => onAssign?.(record, value, reasonText)}
        title={
          dirty && !reasonText && mode.mode !== 'lane'
            ? 'Add a reason first. It goes in the permanent audit ledger.'
            : owner && dirty
              ? `Reassign from ${owner} to ${value} — a human override, recorded in the audit trail`
              : `Assign ${value || 'a rep'} to this record`
        }
      >
        {busy ? '…' : owner ? 'Reassign' : 'Assign'}
      </button>
    </span>
  )
}

/* ---- the bulk bar ----------------------------------------------------- */

/**
 * REASSIGN SELECTED. One rep, applied to every ticked row.
 *
 * Deliberately NOT "apply each row's suggestion in bulk": that would be the
 * engine writing at scale behind one click, which is the thing the governance
 * rule exists to prevent. A bulk reassignment is one human decision — this rep,
 * these records — and it says how many rows already have a different owner
 * before it runs.
 */
export function BulkAssignBar({ selection, gate, onBulk, busy = false, progress = null }) {
  const [rep, setRep] = useState('')
  /* ONE reason for the batch, for the same reason there is one rep for the
   * batch: this bar is a single human decision applied to many rows, and the
   * ledger should record it as one. Per-row reasons here would be the same
   * sentence typed N times. */
  const [reason, setReason] = useState('')
  const rows = selection?.selected || []
  const owned = rows.filter((r) => currentRep(r)).length
  const reasonText = reason.trim()
  const access = useAccessOptional()
  const lane = access?.laneDelegation
  /* A lane delegate's bulk bar: lane members only, and any ticked row outside
   * the lane is SKIPPED AND NAMED rather than sent to be refused. */
  const laneMode = !gate?.allowed && !!lane?.allowed && !!lane?.lane
  const split = useMemo(
    () => {
      const ticked = selection?.selected || []
      return laneMode ? partitionForLane(lane.lane, ticked, rep || (lane.lane.members[0] || '')) : null
    },
    [laneMode, lane, selection?.selected, rep]
  )
  const sendRows = laneMode ? (split?.send || []) : rows
  const ready = !!rep && (laneMode || reasonText.length > 0) && sendRows.length > 0

  /* Also docked to the bottom now, in the same strip as the hunt bar — see
   * .bulk. This was the SECOND bar springing into the flow on first tick, and
   * the click that lands on the wrong row does not care which bar pushed it.
   * Out of flow, it cannot push anything. */
  if (!rows.length) return null

  /* Portalled to <body> for the same reason the hunt dock is: an ancestor
   * `.fade-up` wrapper keeps an identity `transform` after animating, and any
   * transform makes that element — not the viewport — the containing block for
   * fixed children. Docked without a portal, this bar renders ~62,000px down
   * the page. */
  if (!gate?.allowed && !laneMode) {
    return createPortal((
      <div className={styles.bulk}>
        <span className={styles.bulkLocked}>
          {rows.length.toLocaleString()} selected · {gate?.why || 'You can’t manage assignments.'}
        </span>
      </div>
    ), document.body)
  }

  return createPortal((
    <div className={styles.bulk}>
      <span className={styles.bulkCount}>{rows.length.toLocaleString()}</span>
      <span className={styles.bulkLabel}>selected</span>
      <select
        className={styles.select}
        value={rep}
        disabled={busy}
        onChange={(e) => setRep(e.target.value)}
        aria-label="Team member to assign to every selected record"
      >
        <option value="">Reassign to…</option>
        {laneMode
          ? laneOptions(lane.lane).map((n) => <option key={n} value={n}>{n}</option>)
          : <TeamOptions />}
      </select>
      {rep && (
        <input
          type="text"
          className={styles.reason}
          value={reason}
          disabled={busy}
          maxLength={200}
          placeholder={laneMode ? 'Note (optional)' : 'Why this reassignment? (required)'}
          aria-label={`Reason for reassigning ${rows.length} records to ${rep}`}
          title="Written to the permanent assignment ledger against every record in this batch."
          onChange={(e) => setReason(e.target.value)}
        />
      )}
      <button
        type="button"
        className={styles.assignBtn}
        disabled={busy || !ready}
        onClick={() => onBulk?.(sendRows, rep, reasonText)}
        title={rep && !reasonText && !laneMode ? 'Add a reason first. It goes in the permanent audit ledger.' : undefined}
      >
        {busy ? (progress || 'Writing…') : `Reassign ${sendRows.length.toLocaleString()}`}
      </button>
      {laneMode && split?.skip.length > 0 && (
        <span
          className={styles.bulkWarn}
          title={split.skip.slice(0, 12).map((x) => `${x.record?.name || x.record?.id}: ${x.why}`).join('\n')}
        >
          {split.skip.length.toLocaleString()} skipped, outside the {lane.lane.label} lane:{' '}
          {split.skip.slice(0, 3).map((x) => x.record?.name || x.record?.id).join(', ')}
          {split.skip.length > 3 ? ` and ${split.skip.length - 3} more` : ''}
        </span>
      )}
      {owned > 0 && (
        <span className={styles.bulkWarn}>
          {owned.toLocaleString()} of these already have an owner — this overrides them.
        </span>
      )}
    </div>
  ), document.body)
}

/* ---- the history ------------------------------------------------------ */

/** from → to → who → when, for one record. Empty until something changes. */
export function AssignHistory({ recordId, limit = 5 }) {
  const events = useMemo(() => assignmentHistory(recordId).slice(0, limit), [recordId, limit])
  if (!events.length) return null
  return (
    <div className={styles.history}>
      <div className={styles.historyHead}>Assignment history</div>
      {events.map((e) => (
        <div key={e.id} className={styles.historyRow}>
          <span className={styles.historyWhen}>{new Date(e.at).toLocaleString()}</span>
          <span className={styles.historyWhat}>
            {e.before?.sr_assigned_rep || 'Unassigned'} → <strong>{e.after?.sr_assigned_rep || '—'}</strong>
          </span>
          <span className={styles.historyWho}>
            by {e.actor?.name || e.actor?.username || 'unknown'}
            {/* ⚠️ NOT "· failed". A one-word verdict in an audit trail is the
              * same defect as a one-word error toast: it says something went
              * wrong and refuses to say what, on the one surface whose entire
              * job is to explain what happened to a record. The row carries the
              * classified cause, and falls back to naming the status rather
              * than to the bare word. */}
            {e.ok === false && (
              <span className={styles.historyFail} title={e.failure?.message || undefined}>
                {' · '}
                {e.failure?.title || (e.failure?.status
                  ? `not written (server answered ${e.failure.status})`
                  : 'not written, cause not recorded')}
              </span>
            )}
            {e.persisted === false && <span className={styles.historyLocal}> · not persisted server-side</span>}
          </span>
        </div>
      ))}
    </div>
  )
}

/* ---- provenance ------------------------------------------------------- */

/**
 * Where the suggestions come from, and who the picker offers — two different
 * lists, so both are stated rather than left to be assumed equal.
 */
export function MapProvenance() {
  return (
    <span className={styles.prov}>
      <span title="Anyone on the SR team can be assigned a record — a designer, the cheer team, manufacturing — not only the reps with a win history.">
        Picker: <strong>{TEAM_COUNT} team members</strong> · </span>
      Routing map: <strong>{REP_COUNT} reps</strong>
      {MAP_META.generated && <> · built {MAP_META.generated}</>}
      {MAP_META.source && <> · {MAP_META.source}</>}
      {MAP_META.stateCoverage != null && (
        <span
          className={styles.provWarn}
          title="State/conference signals are only as dense as the state backfill. Suggestions get sharper as it completes — the map is read at runtime, so no code changes."
        >
          {' '}· state coverage {MAP_META.stateCoverage}%
        </span>
      )}
    </span>
  )
}
